/**
 * @todo Move most properties up to the `to` level, it's not very relevant from
 *   outside which step of the internal MPM process they belong to.
 */

import { range } from '@epok.tech/fn-lists/range';
import { map } from '@epok.tech/fn-lists/map';
import { reduce } from '@epok.tech/fn-lists/reduce';
import { each } from '@epok.tech/fn-lists/each';
import { wrap } from '@epok.tech/fn-lists/wrap';
import { timer } from '@epok.tech/fn-time';
import { clamp } from '@thi.ng/math/interval';
import { subS2 } from '@thi.ng/vectors/subs';
import { mulN4 } from '@thi.ng/vectors/muln';
import { addN } from '@thi.ng/vectors/addn';
import { rad } from '@thi.ng/math/angle';
import { hsl, srgb } from '@thi.ng/color';
import { MKS } from 'gsl-const';

import gpgpu from '@epok.tech/gl-gpgpu/src';
import { macroPass } from '@epok.tech/gl-gpgpu/src/macros';
import { mapStep } from '@epok.tech/gl-gpgpu/src/maps';
import { toUniforms } from '@epok.tech/gl-gpgpu/src/uniforms';
import { getDrawIndexes, toShape } from '@epok.tech/gl-gpgpu/src/size';
import { getPass } from '@epok.tech/gl-gpgpu/src/step';
import indexForms from '@epok.tech/gl-gpgpu/src/index-forms';

import {
    extensionsHalfFloat as extendHalfFloat, extensionsFloat as extendFloat,
    extensionsDrawBuffers as extendDrawBuffers
  } from '@epok.tech/gl-gpgpu/src/const';
  // @todo Why doesn't this import work...?
  // from '@epok.tech/gl-gpgpu/esm/const';

import gridFrag from './grid/index.frag.glsl';

import gridToPointsFrag from './grid-to-points/index.frag.glsl';

import pointsToGridVert from './points-to-grid/index.vert.glsl';
import pointsToGridFrag from './points-to-grid/index.frag.glsl';

import gridToScreenFrag from './grid-to-screen/index.frag.glsl';

import pointsToScreenVert from './points-to-screen/index.vert.glsl';
import pointsToScreenFrag from './points-to-screen/index.frag.glsl';

const { keys, assign } = Object;
const { floor, min, max, abs, random, sign, sin } = Math;
const { GRAV_ACCEL: g } = MKS;

const cache = {};

export const extensionsHalfFloat = extendHalfFloat;
export const extensionsFloat = extendFloat;
export const extensionsDrawBuffers = extendDrawBuffers;
export const extensionsInstances = () => ['angle_instanced_arrays'];

export const extensions = extensionsHalfFloat;

export const extensionsOptional = () => [
  ...cache.extensionsFloat ??= extensionsFloat(),
  ...cache.extensionsDrawBuffers ??= extensionsDrawBuffers(),
  ...cache.extensionsInstances ??= extensionsInstances()
];

export const optionalExtensions = extensionsOptional;

export function checkStatus(to, f) {
  const { onStatus, status, api: { _gl, gl = _gl } } = to;
  let error;

  to.status = 'wait';

  try {
    f(to);

    const e = gl?.getError();

    if(e) { throw e; }
    else { to.status = 'done'; }
  }
  catch(e) { (to.status = 'fail') && (error = e); }

  (to.status !== 'done') && onStatus?.(to, error);
  to.status = status;
}

/** Convenience for setting up named `gpgpu` fields. */
export function toFields(fields, to = {}) {
  const vs = to.values = [];
  const index = to.index = {};
  const alias = to.alias = [];

  (to.fields = fields).forEach((v, k) => alias[index[k] = vs.push(v)-1] = k);

  return to;
}

/**
 * Convenience for setting up `gpgpu` inputs to link between processes.
 *
 * @todo Handle vertex and fragment shaders.
 * @todo Handle passes.
 */
export function toLink(state, at, to = {}) {
  const uniforms = toUniforms(to.state = state);

  to.macros ??= macroPass(state);

  if(!at) { return to; }

  /** Override uniforms to prefix the right property paths. */
  for(let k in uniforms) {
    const u = uniforms[k];

    uniforms[k] = (c, p, b) => u(c, at(c, p, b), b);
  }

  return to;
}

/**
 * Link all but the bound data in one pass.
 * @todo Ensure the right `bound` value is used for output.
 */
export const linkPast = { stepNow: 0, passNow: 0, bound: 1 };

/** Link all data in one pass. */
export const linkFull =
  { stepNow: 0, passNow: 0, bound: 0, maps: { buffersMax: 0 } };

export function mpm(api, to = {}) {
  console.log('mpm:setup', to);

  to.onStatus ??= (state, error) =>
    console.warn('mpm:status', to.status, to, error);

  const {
      command = api, buffer, elements, clear, hasExtension, now, limits
    } = to.api = api;

  const { pointSizeDims, lineWidthDims } = limits;

  const hasInstances = (cache.extensionsInstances ??= extensionsInstances())
    .every(hasExtension);

  to.support = { hasInstances };

  const vertex = to.vertex = [[1, 1], [1, 0], [0, 0], [0, 1]];
  const quad = to.quad = [[0, 1, 2], [2, 3, 0]];

  // const timestep = to.timer?.step ?? 1e3/120;
  // const timestep = to.timer?.step ?? 1e3/60;
  const timestep = to.timer?.step ?? 1e3/30;
  // const timestep = to.timer?.step ?? 0;

  /** Time control and state. */
  to.timer = timer({
    to,
    now: ((timestep)? undefined : () => now()*1e3),
    period: 1e6,
    ...to.timer,
    // Fixed-step (add-step), or real-time (variable delta-time).
    step: timestep || '-'
  });

  /** Target offset position `x` and `y`, magnitude `z`. */
  // to.g = assign([0, 0, g*1e2], to.g);
  // to.g = assign([0, 0, 0], to.g);
  to.g = assign([0, -1e9, g*1e2], to.g);

  /** Exponential-notation scaling for storage spatial numerical accuracy. */
  const rescale = to.rescale = {
    // mass: [1, 0],
    mass: [1, 0],
    // position: [1, 0],
    position: [1, 0],
    // motion: [1, -6],
    motion: [1, 0],
    // affine: [1, -6],
    affine: [1, 0],
    // deform: [1, -6],
    deform: [1, 0],
    // stress: [1, -6],
    stress: [1, 0],
    ...to.rescale
  };

  /** Header of common settings prepended to each shader. */
  to.head = {
    to,
    test: '',
    kernelSize: 3,
    kernelType: '',
    // kernelType: 'Centre',
    kernelBase: '',
    // kernelBase: 'Cast',
    rescales: keys(rescale),

    glsl(head = to.head) {
      const { test, kernelType, kernelSize, kernelBase, rescales, to } = head;
      const { rescale } = to;

      return ((test)? `#define test ${test}\n` : '')+
        ((kernelSize)? `#define kernelSize ${kernelSize}\n` : '')+
        ((kernelType)? `#define kernelType${kernelType}\n` : '')+
        ((kernelBase)? `#define kernelBase${kernelBase}\n` : '')+
        reduce((rs, r) => {
            const [b, p] = rescale[r];

            return rs+((b*(10**p) === 1)? '' : `#define rescale_${r}\n`);
          },
          rescales, '')+
        `\n`;
    },

    ...to.head
  };

  /** Grid data. */
  const grid = to.grid = {
    to,
    frag: gridFrag,

    detail: 90,

    limit: 1e9,
    /**
     * Remove this ambiguity between `bounds` and `boundsIn`, which are in
     * different reference frames; rename this to `space` or `region`.
     */
    bounds: [-1, -1, 1, 1],

    boundsIn: [1, 1, -1, -1],
    boundsHit: 1,

    on: true,

    ...to.grid
  };

  const { detail: gDetail, limit: gLimit, bounds: gBounds } = grid;

  mulN4(null, gBounds, gLimit);

  const gFields = grid.fields = toFields((new Map())
    /** @todo Use inverse mass to allow for infinite mass objects? */
    .set('mass', 1)
    /** Using the term `motion` since it's variously velocity or momentum. */
    // .set('motionFull', 3)
    .set('motionFull', 2)
    /** Using the term `motion` since it's variously velocity or momentum. */
    // .set('motionBase', 3)
    .set('motionBase', 2));

  const gPre = 'grid_';
  const gDerives = [];
  const { index: gIndex, values: gValues, alias: gAlias } = gFields;

  const { mass: gMass, motionFull: gMotionFull, motionBase: gMotionBase } =
    gIndex;

  gDerives[gMotionFull] = [gMotionFull, gMass];
  gDerives[gMotionBase] = [gMotionBase, gMass];

  const gMaps = { values: gValues, alias: gAlias, derives: gDerives };
  const gSteps = grid.gpgpu?.steps ?? 2;

  const gGPGPU = grid.gpgpu = gpgpu(api, {
    to,
    pre: gPre, maps: gMaps, stepNow: 0, steps: gSteps, stepMax: gSteps,
    side: gDetail,
    clearPass: true,

    macros: { vert: 0 },

    frag: (c, { to: { grid: g, head: h } }) => h.glsl(h)+g.frag,

    uniforms: {
      boundsIn: (c, p) => p.to.grid.boundsIn,
      boundsHit: (c, p) => p.to.grid.boundsHit,
      dt: (c, p) => p.to.timer.dt,
      g: (c, p) => p.to.g,
      rescaleMass: (c, p) => p.to.rescale.mass,
      rescaleMotion: (c, p) => p.to.rescale.motion
    },

    ...grid.gpgpu
  });

  const gMerge = gGPGPU.merge;

  grid.step = (state = grid) => {
    const s = state.onStep?.(state) ?? state;
    const { on, gpgpu } = s;

    on && gpgpu.step(gpgpu);

    return s;
  };

  /** Material points data. */
  const points = to.points = { to };

  const pFields = points.fields = toFields((new Map())
    // .set('position', 3)
    .set('position', 2)
    /** Using the term `motion` since it's variously velocity or momentum. */
    // .set('motion', 3)
    .set('motion', 2)
    .set('mass', 1)
    /**
     * Affine momentum matrix.
     *
     * @todo Need to pack a 3*3 matrix into fewer channels or across texels.
     */
    // .set('affine', 3*3)
    .set('affine', 2*2)
    /**
     * Deformation gradient, its determinant is the volume change.
     *
     * @todo Need to pack a 3*3 matrix into fewer channels or across texels.
     */
    // .set('deform', 3*3)
    .set('deform', 2*2)
    /**
     * Stress gradient, derived from deformation, cached to use across steps.
     *
     * @todo Need to pack a 3*3 matrix into fewer channels or across texels.
     */
    // .set('stress', 3*3)
    .set('stress', 2*2));

  /**
   * The `motion` and `affine` values derive from shared grid data.
   * Other values derive from the next `motion` and `affine` values.
   *
   * @todo In `gl-gpgpu`, add a way to group passes in order if needed, so a
   *   next pass can read values from the past pass if reliant on current
   *   values it `got` from the past pass in the current step.
   * @todo For now, add 2 `gl-gpgpu` processes here if needed:
   *   - If there aren't enough output data channels to do it in one pass,
   *     split the grid-to-points step into 2 parts: update the next values,
   *     then update the values that derive from those in the same step.
   *   - If there are enough output data channels, the step can be combined.
   */
  const gridToPoints = to.gridToPoints = {
    to,
    frag: gridToPointsFrag,

    /**
     * @todo Are mass or volume responsible for particle collision?
     * @todo Move to looking up material types?
     */
    mass: 1,

    // volume: null,
    volume: 5e-4,

    /** Thresholds used to test if the material is separating. */
    volumeSplit: [1e-6, -1e-3],

    /** ASFLIP motion adjustment. */
    motionAdjust: 0.97,

    /** ASFLIP position adjustment range. */
    positionAdjusts: [0, 1],

    /**
     * Mechanics parameters.
     *
     * @todo Move to individual particles looking up material types?
     */

    /** Young's modulus */
    youngMod: 5e5,
    // youngMod: 1e6,
    /** Poisson's ratio. */
    poissonRatio: 0.3,
    // poissonRatio: 0.4,
    /** Bulk modulus. */
    bulkMod: null,
    /** Shear modulus. */
    shearMod: null,

    /**
     * Plasticity parameters.
     *
     * @todo Move to individual particles looking up material types?
     */
    frictionAngle: rad(40),
    // frictionAngle: rad(80),
    // frictionAngle: rad(10),

    materialFriction: null,

    speedClamp: 1e9,
    boundsClamp: true,

    wander: [-1e-5, 1e-2, 1e-6],
    // wander: [1e-5, 1e-2, 1e-6],

    on: true,

    ...to.gridToPoints,

    spawnOn: {
      any: true,
      out: true,
      slow: false,
      centre: 0,
      ...to.gridToPoints.spawnOn
    }
  };

  gridToPoints.volume ??= (() => {
    /** @todo Better volume estimate based on number of particles and size. */
    const { volume: v, volumeScale: vs } = gridToPoints;

    if(v != null) { return v; }

    const [gw, gh] = grid.gpgpu.size.shape;
    // const [pw, ph] = gridToPoints.gpgpu.size.shape;

    return (vs/gw)*(vs/gh);
    // return (vs/(gw/pw))*(vs/(gh/ph));
  })();

  const { index: pIndex, values: pValues, alias: pAlias } = pFields;

  const {
      position: pPosition, motion: pMotion, mass: pMass,
      affine: pAffine, deform: pDeform, stress: pStress
    } = pIndex;

  const pDerives = [];
  /** @todo Move peer-extensions from `derives` to `extends`, avoid redoing. */
  // const pExtends = [];

  pDerives[pPosition] = [pPosition, pMotion, pMass];
  // pExtends[pPosition] = [pAffine, pDeform];

  pDerives[pMotion] = [pMotion, pPosition, pMass];

  pDerives[pMass] = [pMass];

  pDerives[pAffine] = [pPosition, pMass];

  pDerives[pDeform] = [pDeform, pMass];
  // pExtends[pDeform] = [pAffine];

  pDerives[pStress] = [pMass];
  // pExtends[pStress] = [pDeform];

  const pMaps = { values: pValues, derives: pDerives, alias: pAlias };
  const pPre = 'points_';

  /** Inputs to map points data correctly. */

  const gpLinkG = toLink({
      ...linkPast,
      pre: gPre, steps: gSteps, merge: gMerge,
      macros: { output: 0 },
      maps: mapStep({
        ...gMaps, ...linkPast.maps, derives: [[gMotionFull, gMotionBase]]
      })
    },
    (c, p) => p.to.grid.gpgpu);

  gridToPoints.link = { grid: gpLinkG };

  /**
   * @todo Broke this somehow, only 2 steps works, may be a `gpgpu` index issue.
   * @todo Lines don't work for 2 steps but work for 3.
   */
  const pSteps = gridToPoints.gpgpu?.steps ?? 2;
  // const pSteps = gridToPoints.gpgpu?.steps ?? 3;

  /**
   * Grid-to-points `gpgpu` step, gather from `grid` data, render to step
   * `points` data.
   */
  const gpGPGPU = gridToPoints.gpgpu = gpgpu(api, {
    to, pre: pPre, maps: pMaps, stepNow: 0, steps: pSteps, stepMax: pSteps,
    scale: 8,
    macros: { vert: 0 },

    frag: (c, { to: { gridToPoints: { frag: f, link: l }, head: h } }) =>
      h.glsl(h)+l.grid.macros+f,

    uniforms: {
      ...gpLinkG.state.uniforms,
      mass: (c, p) => p.to.gridToPoints.mass,
      volume: (c, p) => p.to.gridToPoints.volume,
      volumeSplit: (c, p) => p.to.gridToPoints.volumeSplit,
      motionAdjust: (c, p) => p.to.gridToPoints.motionAdjust,
      positionAdjusts: (c, p) => p.to.gridToPoints.positionAdjusts,

      bulkMod(c, { to: { gridToPoints: gp } }) {
        const { bulkMod: bm, youngMod: ym, poissonRatio: pr } = gp;

        return bm ?? ym/(3*(1-(pr*2)));
      },
      shearMod(c, { to: { gridToPoints: gp } }) {
        const { shearMod: sm, youngMod: ym, poissonRatio: pr } = gp;

        return sm ?? ym/(2*(1+pr));
      },
      materialFriction(c, { to: { gridToPoints: gp } }) {
        const { materialFriction: mf, frictionAngle: fa } = gp;

        if(mf != null) { return mf; }

        const sfa = sin(fa);

        return (1.633*sfa)/(3-sfa);
      },

      speedClamp: (c, p) => p.to.gridToPoints.speedClamp,
      boundsClamp: (c, p) => +(p.to.gridToPoints.boundsClamp || 0),
      spawnOnAny: (c, p) => +(p.to.gridToPoints.spawnOn.any || 0),
      spawnOnOut: (c, p) => +(p.to.gridToPoints.spawnOn.out || 0),
      spawnOnSlow: (c, p) => +(p.to.gridToPoints.spawnOn.slow || 0),
      spawnOnCentre: (c, p) => +(p.to.gridToPoints.spawnOn.centre || 0),
      wander: (c, p) => p.to.gridToPoints.wander,

      dt: (c, p) => p.to.timer.dt,
      bounds: (c, p) => p.to.grid.bounds,
      chaos: (c, p) => p.to.chaos,
      rescaleMass: (c, p) => p.to.rescale.mass,
      rescalePosition: (c, p) => p.to.rescale.position,
      rescaleMotion: (c, p) => p.to.rescale.motion,
      rescaleAffine: (c, p) => p.to.rescale.affine,
      rescaleDeform: (c, p) => p.to.rescale.deform,
      rescaleStress: (c, p) => p.to.rescale.stress
    },

    ...gridToPoints.gpgpu
  });

  const { size: gpSize, merge: pMerge, bound: pBound } = gpGPGPU;
  const { entries: pl } = gpSize;

  gridToPoints.step = (state = gridToPoints) => {
    const s = state.onStep?.(state) ?? state;
    const { on, gpgpu } = s;

    on && gpgpu.step(gpgpu);

    return s;
  };

  /**
   * Points-to-grid render, splat from `points` data to render `grid` data.
   * @todo How to map `grid` textures correctly in 3x3 quads?
   */
  const pointsToGrid = to.pointsToGrid = {
    to,
    vert: pointsToGridVert,
    frag: pointsToGridFrag,

    stretch: 1, turn: 1,
    on: true,

    /** Straight-alpha additive blending with no multiplying channels. */
    blend: { enable: true, func: { src: 1, dst: 1 } },
    depth: { enable: false },

    ...to.pointsToGrid
  };

  const {
      [gMass]: gMassPass,
      [gMotionFull]: gMotionFullPass, [gMotionBase]: gMotionBasePass
    } = gMaps.valueToPass;

  const pgSize = pointsToGrid.size = {};

  const pgSplats = pointsToGrid.splats = (new Set())
    .add(gMassPass).add(gMotionFullPass).add(gMotionBasePass);

  const pgSplatsL = pgSize.splats = pgSplats.size;

  /**
   * Splats per point, in the shader center on the point and scale to extend
   * as far as the neighbouring 3 nodes.
   *
   * @todo Draw to all the needed grid passes, framebuffer-per-splat.
   */
  const pgInstancesL = pgSize.instances = pl*pgSplatsL;

  /**
   * Inputs to map points data correctly.
   *
   * @todo Handle vertex and fragment shaders.
   * @todo Handle passes.
   */

  const pgLinkP = toLink({
      ...linkPast,
      pre: pPre, steps: pSteps, merge: pMerge,
      macros: { output: 0 },
      maps: mapStep({
        ...pMaps, ...linkPast.maps,
        derives: [[pPosition, pMotion, pMass, pAffine, pStress]]
      })
    },
    (c, p) => p.to.gridToPoints.gpgpu);

  const pgLinkG = toLink({
      ...linkPast,
      pre: gPre, steps: gSteps, merge: gMerge,
      macros: { samples: 0, taps: 0 },
      maps: mapStep({ ...gMaps, ...linkPast.maps, derives: null })
    },
    (c, p) => p.to.grid.gpgpu);

  pointsToGrid.link = { points: pgLinkP, grid: pgLinkG };

  /** If using points, they're square and seem small, seems just big render? */
  // pointsToGrid.quads = true;
  // pointsToGrid.quads = false;
  // pointsToGrid.instanced = false;
  // pointsToGrid.instanced = true;
  // pointsToGrid.batch = true;
  // pointsToGrid.batch = false;
  // pointsToGrid.order = 'point-splat';
  // pointsToGrid.order = 'splat-point';

  /** Whether to draw using quads or points. */
  const pgQuads = pointsToGrid.quads ??= (pointSizeDims[1] < 3);
  /** Whether to draw using instancing. */
  const pgInstanced = pointsToGrid.instanced ??= hasInstances && pgQuads;
  /** Iterate, whether to batch-draw. */
  const pgBatch = pointsToGrid.batch ??= !pgInstanced && (pgSplatsL > 1);

  /** Iterate, splats-then-points or points-then-splats. */
  const pgOrder = pointsToGrid.order ??=
    ((pgInstanced)? 'point-splat' : 'splat-point');

  /** The form to use, suitable for `elements`. */
  const pgForm = pointsToGrid.form ??= ((pgQuads)? quad : null);
  /** The number of parts per-form. */
  const pgFormsL = pgSize.forms = pgForm?.length ?? 1;
  /** The number of vertexes per-form. */
  const pgFormVertexesL = pgSize.formVertexes = pgForm?.[0]?.length ?? 1;

  /** The form vertex source to use. */
  const pgFormSource = pointsToGrid.formSource ??=
    ((pgQuads)? vertex : [[0.5, 0.5]]);

  /** The number of vertexes per-source. */
  const pgFormSourcesL = pgSize.formSources = pgFormSource.length;

  /** The number of vertexes per-point. */
  pgSize.pointVertexes = pgFormsL*pgFormVertexesL;

  /** The number of sources overall. */
  const pgFormSourcesSplatsL = pgSize.formSourcesSplats =
    pgFormSourcesL*pgSplatsL;

  /** The number of sources overall. */
  const pgInstancesSourceL = pgSize.instancesSources =
    pgInstancesL*pgFormSourcesL;

  /**
   * @todo Change this to a grid `gpgpu`, overriding all the attributes and
   *   both shaders, but reusing the framebuffer passes logic to draw all the
   *   needed grid passes correctly.
   */
  pointsToGrid.pass = command(pointsToGrid.pipeline = {
    vert: (c, { vert: v, quads: q, link: { points, grid }, to: { head: h } }) =>
      h.glsl(h)+
      ((q)? '#define points_grid_quads\n\n' : '')+
      points.macros+grid.macros+v,

    frag: (c, { frag: f, quads: q, link: l, to: { head: h } }) =>
      h.glsl(h)+
      ((q)? '#define points_grid_quads\n\n' : '')+
      l.grid.macros+f,

    primitive: (c, p) => ((p.quads)? 'triangles' : 'points'),

    attributes: {
      /** Vertex only for drawing quads. */
      vertex: ((!pgQuads)? pointsToGrid.vertex = { constant: pgFormSource }
        : buffer(pointsToGrid.vertex = ((pgInstanced || pgBatch)? pgFormSource
            : map((v, i) => wrap(i, pgFormSource),
                range(pgInstancesSourceL), 0)))),

      /** Index of each point. */
      index: ((pgInstanced)?
          {
            ...(pointsToGrid.index = ((pgOrder === 'splat-point')?
              // Splats-then-points, more data overall.
              {
                buffer: map((v, i) => i, range(pl), 0),
                divisor: pgSplatsL
              }
            : // Points-then-splats, less data overall.
              {
                buffer: map((v, i) => i%pl, range(pgInstancesL), 0),
                divisor: 1
              })),
            buffer: buffer(pointsToGrid.index.buffer)
          }
        : (pgBatch)?
          (pgOrder === 'splat-point') &&
            // Splats-then-points, batches.
            ((c, p, b) =>
              (pointsToGrid.index ??= [])[b] ??=
                { constant: floor(b/p.splats.size) })
        : (pgOrder === 'splat-point') &&
            // Splats-then-points, once, more data overall.
            buffer(pointsToGrid.index =
              map((v, i) => floor(i/pgFormSourcesSplatsL),
                range(pgInstancesSourceL), 0))),

      /** Index of each splat. */
      splat: ((pgInstanced)?
          {
            ...(pointsToGrid.splat = ((pgOrder === 'splat-point')?
              // Splats-then-points, more data overall.
              {
                buffer: map((v, i) => i%pgSplatsL, range(pgInstancesL), 0),
                divisor: 1
              }
            : // Points-then-splats, less data overall.
              {
                buffer: map((v, i) => i, range(pgSplatsL), 0),
                divisor: pl
              })),
            buffer: buffer(pointsToGrid.splat.buffer)
          }
        : (pgBatch)?
          (pgOrder === 'splat-point') &&
            // Splats-then-points, batches, less data overall.
            ((c, p, b) =>
              (pointsToGrid.splat ??= [])[b] ??= { constant: b%p.splats.size })
        : (pgOrder === 'splat-point') &&
            // Splats-then-points, once, more data overall.
            buffer(pointsToGrid.splat =
              map((v, i) => i%pgFormSourcesSplatsL,
                range(pgInstancesSourceL), 0)))
    },
    uniforms: {
      ...pgLinkP.state.uniforms, ...pgLinkG.state.uniforms,
      stretch: (c, p) => p.stretch,
      turn: (c, p) => p.turn,
      bounds: (c, p) => p.to.grid.bounds,
      rescaleMotion: (c, p) => p.to.rescale.motion,
      rescaleMass: (c, p) => p.to.rescale.mass,
      rescaleAffine: (c, p) => p.to.rescale.affine,
      rescaleStress: (c, p) => p.to.rescale.stress,

      // Test
      loop: (c, p) => p.to.timer.loop
    },
    blend: {
      enable: (c, p) => p.blend.enable,
      func: {
        srcRGB: (c, { blend: { func: { src: o, srcRGB: s } } }) => s ?? o,
        srcAlpha: (c, { blend: { func: { src: o, srcAlpha: s } } }) => s ?? o,
        dstRGB: (c, { blend: { func: { dst: o, dstRGB: s } } }) => s ?? o,
        dstAlpha: (c, { blend: { func: { dst: o, dstAlpha: s } } }) => s ?? o
      }
    },

    depth: { enable: (c, p) => p.depth.enable },

    /** A form at a time; or all forms together. */
    elements: ((!pgForm)? pointsToGrid.elements = null
      : elements(pointsToGrid.elements =
        ((pgInstanced || pgBatch)? pgForm
        : map((v, i) =>
              addN([], wrap(i, pgForm), floor(i/pgFormsL)*pgFormSourcesL),
            range(pgInstancesL*pgFormsL), 0)))),

    count: (c, { instanced, batch, size: { pointVertexes, instances } }) =>
      pointVertexes*((instanced || batch)? 1 : instances),

    instances: (c, { instanced: i, size: s }) => ((i)? s.instances : 1),

    framebuffer: (c, { to, framebuffer: f = getPass(to.grid.gpgpu) }) =>
      f?.framebuffer
  });

  /** @todo Draw to all the needed grid passes, framebuffer-per-splat. */
  pointsToGrid.step = (state = pointsToGrid) => {
    const s = state.onStep?.(state) ?? state;
    const { on, batch: b, pass, size, to } = s;

    if(!on) { return s; }

    const { gpgpu: gGPGPU, fields: gf } = to.grid;
    let p = s;

    b && range(p = s.batchStates ??= [], s, 0, p.length = size.instances);
    pass(p);
    gGPGPU.merge?.update?.(gGPGPU);

    return s;
  };

  /** Render to the screen. */
  const screen = to.screen = {
    to,
    size: [1, 1], aspect: [1, 1],
    clears: { color: [0, 0, 0, 0], depth: 1 },
    clear: (s = screen) => clear(s.clears)
  };

  /** Render the grid to the screen. */
  const gridToScreen = to.gridToScreen = {
    to,
    frag: gridToScreenFrag,

    paceLinear: false,

    /** Squared or linear pace, picked by `paceLinear`. */
    ranges: [
      // [0, 9e12],
      [0, 1e10],
      // [0, 9e6]
      [0, 1e5]
    ],

    /**
     * Squared or linear pace, picked by `paceLinear`.
     *
     * @see [Y-for-X in Pomax's guide](https://pomax.github.io/bezierinfo/#yforx)
     * @see [Finding Bézier roots and Newton-Raphson method in Pomax's guide](https://pomax.github.io/bezierinfo/#extremities)
     */
    curves: [
      // [[0, 0], [0.2, 0.8], [1, 1], [1, 1]],
      map((v, i, a) => range(2, i/(a.length-1)), range(4), 0),
      // [[0, 0], [0.5, 0.2], [0.9, 0.9], [1, 1]]
      map((v, i, a) => range(2, i/(a.length-1)), range(4), 0)
    ],

    // colorHi: srgb('#0055ff55').buf,
    colorHi: [1, 1, 1, 0.15],
    colorLo: [0, 0, 0, 0],
    angleHSLA: hsl('hsla(0deg, 80%, 70%, 0.5)').buf,
    // angleHSLA: [0, 0, 0, 0],

    on: true,

    ...to.gridToScreen
  };

  /** Inputs to map grid data correctly. */

  const gsLinkG = toLink({
      ...linkFull,
      /**
       * Draw all states with none bound as outputs.
       * @todo Errors without `merge`; why, if the framebuffer isn't bound?
       */
      bound: +(!gMerge),
      pre: gPre, steps: gSteps, merge: gMerge,
      macros: { output: 0 },
      // maps: mapStep({ ...gMaps, ...linkFull.maps, derives: [[gMotionFull]] })
      maps:
        mapStep({ ...gMaps, ...linkFull.maps, derives: [[gMotionFull, gMass]] })
      // maps: mapStep({
      //   ...gMaps, ...linkFull.maps,
      //   derives: [[{ value: gMotionFull, step: 1 }, { value: gMass, step: 1 }]]
      // })
    },
    (c, p) => p.to.grid.gpgpu);

  gridToScreen.link = { grid: gsLinkG };

  gridToScreen.pass = command(gridToScreen.pipeline = {
    vert: (c, { link: l, to: { head: h, grid: g } }) =>
      h.glsl(h)+l.grid.macros+g.gpgpu.vert,

    frag: (c, { frag: f, link: l, to: { head: h }, angleHSLA, paceLinear }) =>
      h.glsl(h)+l.grid.macros+
      ((angleHSLA?.[3])? '#define angleHSLA angleHSLA\n\n' : '')+
      ((paceLinear)? '#define paceLinear\n\n' : '')+
      f,

    attributes: { [gPre+'position']: gGPGPU.positions },
    uniforms: {
      ...gsLinkG.state.uniforms,
      angleHSLA: (c, p) => p.angleHSLA,
      range: (c, { ranges: rs, paceLinear: p }) => rs[+p],

      ...reduce((to, _, i) => {
          to[`curve[${i}]`] = (c, { curves: cs, paceLinear: p }) => cs[+p][i];

          return to;
        },
        gridToScreen.curves[0], {}),

      colorHi: (c, p) => p.colorHi,
      colorLo: (c, p) => p.colorLo,

      aspect: (c, p) => p.to.screen.aspect,
      rescaleMotion: (c, p) => p.to.rescale.motion
    },
    /** @see [Premultiplied vs straight-alpha blending](https://limnu.com/webgl-blending-youre-probably-wrong/) */
    blend: { enable: true, func: { src: 1, dst: 'one minus src alpha' } },
    depth: { enable: false },
    count: gGPGPU.count,
    framebuffer: (c, p) => p.framebuffer
  });

  gridToScreen.draw = (state = gridToScreen) => {
    const s = state.onDraw?.(state) ?? state;
    const { on, pass } = s;

    on && pass(s);

    return s;
  };

  /**
   * Render the points to the screen.
   *
   * @todo Set up a separate `gpgpu` with its own steps, to copy point position
   *   and/or advect from the grid while staying close to source point, to track
   *   many steps of state  without using loads of extra data.
   */
  const pointsToScreen = to.pointsToScreen = {
    to,
    vert: pointsToScreenVert,
    frag: pointsToScreenFrag,

    form: 1,
    // form: 2,

    // colorHi: srgb('#ff4400cc').buf,
    colorHi: [1, 1, 1, 0.5],

    // colorLo: srgb('#75000066').buf,
    colorLo: [0, 0, 0, 0],

    colorImage: null,
    colorBy: [0, 0],
    flip: [1, -1],
    offset: [0, 0, 0, 0],
    aspectImage: [1, 1],

    useDiscard: false,
    lengthLinear: false,

    wideHi: [4e-3, 4e-6],
    // wideHi: [6e-3, 6e-6],
    // wideHi: [6e-2, 6e-4],
    // wideHi: [511, 255],
    // wideHi: [pointSizeDims[1], pointSizeDims[1]],

    wideLo: [2e-3, 2e-6],
    // wideLo: [2e-3, 2e-6],
    // wideLo: [2e-2, 2e-4],
    // wideLo: [1, 1e-1],
    // wideLo: [pointSizeDims[0], pointSizeDims[0]],

    widths: [, pointSizeDims, lineWidthDims],
    primitives: [, 'points', 'lines'],

    /** @see [Premultiplied vs straight-alpha blending](https://limnu.com/webgl-blending-youre-probably-wrong/) */
    // blend: { enable: true, func: { src: 1, dst: 'one minus src alpha' } },
    blend: { enable: true, func: { src: 1, dst: 1 } },
    depth: { enable: false },
    count: null,

    on: true,

    ...to.pointsToScreen
  };

  /** Inputs to map points data correctly. */

  /**
   * Draw all states with none bound as outputs.
   *
   * @todo Errors without `merge`; why, if the framebuffer isn't bound?
   * @todo Lines don't work for 2 steps but work for 3.
   */
  const psLinkPBound = +(!pMerge);
  const psDrawSteps = pSteps-psLinkPBound;

  const psLinkP = toLink({
      ...linkFull,
      bound: psLinkPBound,
      pre: pPre, steps: pSteps, merge: pMerge,
      macros: { output: 0 },
      maps: mapStep({
        ...pMaps, ...linkFull.maps, derives: [[pPosition, pMass]]
      })
    },
    (c, p) => p.to.gridToPoints.gpgpu);

  pointsToScreen.link = { points: psLinkP };

  /** @todo Lines don't work for 2 steps but work for 3. */
  const psCounts = pointsToScreen.counts =
    map((_, f) => f && indexForms(psDrawSteps, f, pl), range(3), 0);

  pointsToScreen.pass = command(pointsToScreen.pipeline = {
    vert: (c, { vert: v, colorImage: ci, link: l, to: { head: h } }) =>
      h.glsl(h)+
      ((ci)? '#define colorImage colorImage\n\n' : '')+
      l.points.macros+
      v,

    frag: (c, { frag: f, link, to: { head: h }, useDiscard, lengthLinear }) =>
      h.glsl(h)+link.points.macros+
      ((useDiscard)? '#define useDiscard\n\n' : '')+
      ((lengthLinear)? '#define lengthLinear\n\n' : '')+
      f,

    primitive: (c, { form: f, primitives: ps }) => ps[f],
    attributes: { index: getDrawIndexes(max(...psCounts)) },

    uniforms: {
      ...psLinkP.state.uniforms,
      form: (c, p) => p.form,
      colorHi: (c, p) => p.colorHi,
      colorLo: (c, p) => p.colorLo,

      colorImage: (c, p) => p.colorImage,
      colorBy: (c, p) => p.colorBy,
      flip: (c, p) => p.flip,
      offset: (c, p) => p.offset,
      aspectImage: (c, p) => p.aspectImage,

      wideHi: (c, { wideHi: wls, lengthLinear: l, widths: wfs, form: f, to }) =>
        clamp(wls[+l]*min(...to.screen.size), ...wfs[f]),

      wideLo: (c, { wideLo: wls, lengthLinear: l, widths: wfs, form: f, to }) =>
        clamp(wls[+l]*min(...to.screen.size), ...wfs[f]),

      widths: (c, { widths: wfs, form: f }) => wfs[f],

      size: (c, p) => p.to.screen.size,
      aspectScreen: (c, p) => p.to.screen.aspect,
      dt: (c, p) => p.to.timer.dt,
      bounds: (c, p) => p.to.grid.bounds,
      rescaleMass: (c, p) => p.to.rescale.mass,
      rescalePosition: (c, p) => p.to.rescale.position
    },

    lineWidth: (c, { wideHi: wls, lengthLinear: l, widths: wfs, to }) =>
      clamp(wls[+l]*min(...to.screen.size), ...wfs[2]),

    blend: {
      enable: (c, p) => p.blend.enable,
      func: {
        srcRGB: (c, { blend: { func: { src: o, srcRGB: s } } }) => s ?? o,
        srcAlpha: (c, { blend: { func: { src: o, srcAlpha: s } } }) => s ?? o,
        dstRGB: (c, { blend: { func: { dst: o, dstRGB: s } } }) => s ?? o,
        dstAlpha: (c, { blend: { func: { dst: o, dstAlpha: s } } }) => s ?? o
      }
    },

    depth: { enable: (c, p) => p.depth.enable },
    count: (_, { count: c, counts: cs, form: f }) => c ?? cs[f],
    framebuffer: (c, p) => p.framebuffer
  });

  pointsToScreen.draw = (state = pointsToScreen) => {
    const s = state.onDraw?.(state) ?? state;
    const { on, pass } = s;

    on && pass(s);

    return s;
  };

  /** Which parts of a `pass` are used. */
  to.uses = {
    /** Whether a `pass` ticks the `timer`. */
    tick: true,
    /** Whether a `pass` clears the screen. */
    clear: true,
    /** Whether a `pass` updates the data. */
    update: true,
    /** Whether a `pass` draws to the screen. */
    draw: true,
    /** Whether a `pass` runs any post-processes or cleanup. */
    post: true,
    log: false,
    ...to.uses
  };

  /** How much a `pass` steps each `gpgpu` before the next links use them. */
  to.linksBy ??= 1;
  // to.linksBy ??= 0;
  /** How many partial-time-step `pass`es to do per-`step` for accuracy. */
  // to.passes ??= 1;
  // to.passes ??= 4;
  to.passes ??= 6;
  // to.passes ??= 9;
  to.passAt = 0;

  to.tick = (state = to) => {
    const s = state.onTick?.(state) ?? state;
    const { uses, timer: t } = s;

    if(!uses.tick) { return s; }

    const { time, period: p } = timer(t);

    t.loop = abs(((time+p)%(p*2))-p);

    return s;
  };

  to.clear = (state = to) => {
    const s = state.onClear?.(state) ?? state;
    const { uses, screen } = s;

    uses.clear && screen.clear(screen);

    return s;
  };

  const logData = (pre, suf, framebuffer) =>
    console.log('mpm:'+pre+':'+suf, pre+'-'+framebuffer?._framebuffer?.id);

  to.update = (state = to) => {
    const s = state.onUpdate?.(state) ?? state;
    const { uses, pointsToGrid: pg, grid: g, gridToPoints: gp, linksBy } = s;
    const { update, log } = uses;

    if(!update) { return s; }

    const { step: pgStep, pipeline: pgPipe } = pg;
    const { step: gStep, gpgpu: gGPGPU } = g;
    const { pipeline: gPipe, clear: gClear } = gGPGPU;
    const { step: gpStep, gpgpu: gpGPGPU } = gp;
    const { pipeline: gpPipe } = gpGPGPU;

    s.chaos = random();
    /** Clear the grid output, as `pointsToGrid` can use additive blending. */
    gClear(gGPGPU);
    log && logData('g', 'clear', getPass(gGPGPU)?.framebuffer);
    /** Update the data from points-to-grid. */
    pgStep(pg);
    log && logData('pg', 'step', pgPipe.framebuffer(0, pg));
    /** Update the data from grid-to-grid. */
    gStep(g);
    log && logData('g', 'step', gPipe.framebuffer(0, gGPGPU));
    /** Advance `stepNow` for any next linked steps. */
    linksBy && gGPGPU.stepBy(gGPGPU, linksBy);
    log && logData('g', 'stepBy', gPipe.framebuffer(0, gGPGPU));
    /** Update the data from grid-to-points. */
    gpStep(gp);
    log && logData('gp', 'step', gpPipe.framebuffer(0, gpGPGPU));
    /** Advance `stepNow` for any next linked steps. */
    linksBy && gpGPGPU.stepBy(gpGPGPU, linksBy);
    log && logData('gp', 'stepBy', gpPipe.framebuffer(0, gpGPGPU));

    return s;
  };

  /** @todo Move reset logic from grid-to-points shader to its own process. */
  to.reset = (state = to) => {
    const s = state.onReset?.(state) ?? state;
    const { gridToPoints: gp, update } = s;
    const { gpgpu: gpGPGPU } = gp;
    const c = gpGPGPU.clearPass;

    /** @todo Make this less awkward. */
    gpGPGPU.clearPass ??= gp.clears;
    gp.clears = gpGPGPU.clear(gpGPGPU).clearPass;
    gpGPGPU.clearPass = c;

    update(s);

    return s;
  };


  to.draw = (state = to) => {
    const s = state.onDraw?.(state) ?? state;
    const { uses, gridToScreen, pointsToScreen } = s;

    if(!uses.draw) { return s; }

    gridToScreen.draw(gridToScreen);
    pointsToScreen.draw(pointsToScreen);

    return s;
  };

  to.post = (state = to) => {
    const s = state.onPost?.(state) ?? state;
    const { uses, grid, gridToPoints, linksBy } = s;

    if(!uses.post) { return s; }

    /** Reset `stepNow` for any next linked steps. */
    if(linksBy) {
      const gGPGPU = grid.gpgpu;
      const gpGPGPU = gridToPoints.gpgpu;

      gGPGPU.stepBy(gGPGPU, -linksBy);
      gpGPGPU.stepBy(gpGPGPU, -linksBy);
    }

    return s;
  };

  to.pass = (state = to) => {
    const s = state.onPass?.(state) ?? state;
    const { tick, clear, update, draw, post, uses: { log } } = s;

    log && console.groupCollapsed('mpm:pass:'+Date.now());
    tick(s);
    clear(s);
    update(s);
    draw(s);
    post(s);
    log && console.groupEnd();

    return s;
  };

  to.step = (state = to) => {
    const s = state.onStep?.(state) ?? state;
    const { passes, pass, timer, tick, clear, update, draw, post, uses } = s;

    if(passes === 1) { return pass(s); }

    const { log } = uses;
    const { time: t0 } = timer;
    const { time: t1, dt } = tick(s).timer;
    const dtp = timer.dt = dt/passes;

    log && console.groupCollapsed('mpm:step:'+Date.now());

    // Data passes, no draw, sub-steps.
    timer.time = t0;
    clear(s);

    for(s.passAt = 0; s.passAt < passes; ++s.passAt) {
      timer.time += dtp;
      update(s);
    }

    // Draw pass, no data, full-step.
    timer.time = t1;
    timer.dt = dt;
    s.passAt = 0;
    draw(s);
    post(s);

    log && console.groupEnd();

    return s;
  };

  to.frame = () => to.step();

  return to;
}

export default mpm;
